
export function getSiteTypes(sites: Array<any>) {

  const hasProsumage = sites.includes('prosumage');
  const hasProsumer = sites.includes('prosumer');
  const hasConsumer = sites.includes('consumer');
  const hasProducer = sites.includes('producer');

  const comboKey = [
    hasProsumage ? '1' : '0',
    hasProsumer ? '1' : '0',
    hasConsumer ? '1' : '0',
    hasProducer ? '1' : '0',
  ].join('');

  const combinationMap = {
    '1111': 'default',
    '0000': 'default',
    '0010': 'consumer',
    '0001': 'producer',
    '0100': 'prosumer',
    '0111': 'prosumer',
    '0101': 'prosumer',
    '0110': 'prosumer',
    '0011': 'consumer_producer',
    '1000': 'prosumer_battery',
    '1010': 'prosumer_battery',
    '1001': 'prosumer_battery',
    '1100': 'prosumer_battery',
    '1011': 'prosumer_battery',
    '1101': 'prosumer_battery',
    '1110': 'prosumer_battery',
  };

  return combinationMap[comboKey] ?? 'default';

}

export function getSiteLayoutHelper(metadata: any, pathSegment: string, params: any, activeModule, MODULE) {

  let siteType =
    (metadata.selectedEntity as any)?.siteType?.[0]?.siteType ||
    (metadata.selectedEntity as any)?.type ||
    (metadata.selectedEntity as any)?.siteType ||
    null;



  // FLEXO-6105 §2: the end-user "All sites" aggregate lives on the bare `/member`
  // route. Unlike single-entity pages, its layout must be derived from the WHOLE
  // set of the member's sites — otherwise it always fell through to 'consumer'
  // (no branch handled `member`), hiding the prosumer layout for members that own
  // prosumer sites. Aggregate the site types the same way `member-energy` does.
  if (pathSegment === 'member') {
    let member: any = metadata.selectedEntity || params.id;
    if (typeof member === 'string' || !member) {
      member =
        (metadata?.entities?.member || []).find(
          (m: any) => m.memberId === params.id
        ) || member;
    }

    // End-user member objects (from `entities`) don't reliably carry their full
    // `sites` array — the EndUserMemberResolver derives them by combining
    // `member.sites` / changelog with `entities.site` filtered by the member.
    // Mirror that here (inlined to avoid a templates -> resolvers dependency
    // cycle) so the aggregate layout is computed over the member's real sites.
    const allSites = (metadata?.entities as any)?.site || [];
    const memberId = (member as any)?.memberId || params.id;
    const ownSiteIds = new Set(
      [
        ...(((member as any)?.sites || []).map((s: any) => s?.siteId)),
        ...(((member as any)?.changelog || []).map((c: any) => c?.siteId)),
      ].filter(Boolean)
    );
    const memberSites = Array.from(
      new Map(
        [
          ...allSites.filter((s: any) =>
            ownSiteIds.size > 0
              ? ownSiteIds.has(s?.siteId)
              : s?.memberId === memberId
          ),
          ...(((member as any)?.sites || []).filter((s: any) => s?.siteId)),
        ]
          .filter((s: any) => s?.siteId)
          .map((s: any) => [s.siteId, s])
      ).values()
    );

    if (memberSites.length === 1) {
      siteType =
        memberSites[0]?.siteType?.[0]?.siteType ||
        memberSites[0]?.siteType ||
        memberSites[0]?.type ||
        null;
    } else if (memberSites.length > 1) {
      const sitesTypes = memberSites.map(
        (site: any) =>
          site.siteType?.[0]?.siteType || site.siteType || site.type || null
      );
      siteType = getSiteTypes(sitesTypes);
    }
  }

  if (pathSegment === 'member-energy') {

    let member = metadata.selectedEntity || params.id;
    if (typeof member === 'string' || !member) {
      const _members = [...(metadata?.sortedEntities?.communities.reduce(
        (prev: any, next: any) => ([...prev, ...(next?.members || [])]), []) || []),
      ...(metadata?.sortedEntities?.unassignedMembers || [])
      ];

      // A multi-community member appears once per community, each copy scoped to
      // that community's sites (see structureData). Aggregate across copies so
      // member.sites is the member's full footprint (deduped by siteId);
      // otherwise a member with sites in several communities is mis-detected as
      // single-site below.
      const _copies = _members.filter((m: any) => m?.memberId === params.id);
      member = _copies.length
        ? {
            ..._copies[0],
            sites: [
              ...new Map(
                _copies
                  .flatMap((m: any) => m?.sites || [])
                  .map((s: any) => [s?.siteId, s])
              ).values(),
            ],
          }
        : undefined;
    }


    if ((member as any)?.sites?.length === 1) {
      siteType = (member as any)?.sites[0]?.siteType?.[0]?.siteType
        || (member as any)?.sites[0]?.siteType
        || (member as any)?.sites[0]?.type
        || null;


    } else {

      const sitesTypes = (member as any)?.sites?.map((site: any) => site.siteType?.[0]?.siteType || site.siteType || site.type || null) || [];

      siteType = getSiteTypes(sitesTypes);


    }

  }

  if (pathSegment === 'member-economics') {

    let member = params.id;
    if (typeof member === 'string') {
      const _members = [...(metadata?.sortedEntities?.communities.reduce(
        (prev: any, next: any) => ([...prev, ...(next?.members || [])]), []) || []),
      ...(metadata?.sortedEntities?.unassignedMembers || [])
      ];

      member = _members.find((member: any) => member.memberId === (params.id || metadata?.selectedEntity));
    }

  }

  if (pathSegment === 'community-energy') {

    let community = metadata.selectedEntity || params.id;
    if (typeof community === 'string' || !community) {
      community = (metadata?.entities?.community || [])?.find((community: any) => community.communityId === (params.id));
    }

    const communitySites = metadata?.entities?.site?.filter((site: any) => site.communityId === (community?.communityId || params.id)) || [];

    if ((communitySites as any)?.length === 1) {
      siteType = communitySites[0]?.siteType?.[0]?.siteType
        || communitySites[0]?.siteType
        || communitySites[0]?.type
        || null;


    } else {

      const sitesTypes = communitySites?.map((site: any) => site.siteType?.[0]?.siteType || site.siteType || site.type || null) || [];

      siteType = getSiteTypes(sitesTypes);


    }

  }

  if (pathSegment === 'community-economics') {

    const community = params.id;
    const communitySites = metadata?.entities?.site?.filter((site: any) => site.communityId === community) || [];

  }

  if (siteType === null && pathSegment?.includes('site')) {
    siteType =
      (metadata?.selectedEntity as any)?.siteType?.[0]?.siteType ||
      (metadata?.selectedEntity as any)?.siteType ||
      (metadata?.selectedEntity as any)?.type ||
      (metadata?.entities as any)?.site?.find(
        (site: any) => site.siteId === params.id
      )?.siteType?.[0]?.siteType ||
      null;
  }

  if (
    siteType === null &&
    (pathSegment?.includes('site') ||
      pathSegment === 'household' || pathSegment === '') &&
    (metadata?.entities as any)?.site?.length > 0
  ) {
    siteType =
      (metadata?.entities as any)?.site?.[0]?.siteType?.[0]
        ?.siteType ||
      (metadata?.entities as any)?.site?.[0]?.type ||
      null;
  }

  // siteType = 'prosumer';
  siteType = siteType || 'consumer';
  return siteType;

}
